/**
 * useRelatorioGeraisData - Data fetching and report selection for general reports
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import apiClient from '../services/apiClient';
import {
  getAgendamentos,
  getConsolidadoQtde,
  getUltimas3Contagens,
  getUltimas3Detalhado,
  getConsolidadoDetalhado,
  getSegregacaoConsolidado,
  getSolicitacaoColeta,
  getConsolidadoLeitura,
  downloadAgendamentosExcel,
  downloadConsolidadoQtdeExcel,
  downloadConsolidadoQtdePdf,
  downloadUltimas3Excel,
  downloadUltimas3DetalhadoExcel,
  downloadConsolidadoDetalhadoExcel,
  downloadSegregacaoConsolidadoExcel,
  downloadSolicitacaoColetaExcel,
  downloadConsolidadoLeituraExcel,
} from '../services/relatorioGeraisService';
import type { RelatorioFilters } from '../services/relatorioGeraisService';

export type ReportId =
  | 'agendamentos'
  | 'consolidado-qtde'
  | 'ultimas-3-contagens'
  | 'ultimas-3-detalhado'
  | 'consolidado-detalhado'
  | 'segregacao-consolidado'
  | 'solicitacao-coleta'
  | 'consolidado-leitura';

export interface FilterOption {
  value: number;
  label: string;
}

export function useRelatorioGeraisData() {
  const [selectedReport, setSelectedReport] = useState<ReportId | null>(null);
  const [reportData, setReportData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  // Pagination for consolidado-detalhado
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);

  // Store last filters for auto-fetch on page change
  const lastFiltersRef = useRef<RelatorioFilters | null>(null);

  // Filter options loaded from API
  const [hospitalOptions, setHospitalOptions] = useState<FilterOption[]>([]);
  const [clienteOptions, setClienteOptions] = useState<FilterOption[]>([]);
  const [auditorOptions, setAuditorOptions] = useState<FilterOption[]>([]);

  // Load filter options on mount
  useEffect(() => {
    const loadOptions = async () => {
      try {
        const [hospitaisRes, clientesRes, auditoresRes] = await Promise.all([
          apiClient.get('/hospitais').catch(() => ({ data: [] })),
          apiClient.get('/clientes').catch(() => ({ data: [] })),
          // #11: o endpoint correto e auditores-disponiveis ('/users/auditores/'
          // dava 404 e zerava o filtro de auditor).
          apiClient.get('/users/auditores-disponiveis').catch(() => ({ data: [] })),
        ]);

        // #11: aceita array, {results} OU {items} (robustez a paginacao).
        const unwrap = (d: any) => Array.isArray(d) ? d : (d?.results || d?.items || []);
        const cnpjDigits = (v: any) => String(v || '').replace(/\D/g, '');

        const hospitais = unwrap(hospitaisRes.data);
        setHospitalOptions(hospitais.map((h: any) => ({
          value: h.id,
          label: h.nome_fantasia || h.razao_social || `Hospital #${h.id}`,
          // #11: permite buscar por nome OU CNPJ (com e sem mascara).
          searchValue: `${h.nome_fantasia || ''} ${h.razao_social || ''} ${h.cnpj || ''} ${cnpjDigits(h.cnpj)}`,
        })));

        const clientes = unwrap(clientesRes.data);
        setClienteOptions(clientes.map((c: any) => ({
          value: c.id,
          label: c.nome_fantasia || c.razao_social || `Cliente #${c.id}`,
          searchValue: `${c.nome_fantasia || ''} ${c.razao_social || ''} ${c.cnpj || ''} ${cnpjDigits(c.cnpj)}`,
        })));

        const auditores = unwrap(auditoresRes.data);
        setAuditorOptions(auditores.map((a: any) => ({
          value: a.id,
          label: a.full_name || a.username || `Auditor #${a.id}`,
        })));
      } catch {
        // Silently fail - options just won't be available
      }
    };
    loadOptions();
  }, []);

  const fetchReportPage = useCallback(async (
    report: ReportId,
    filters: RelatorioFilters,
    targetPage: number,
  ) => {
    setLoading(true);
    setError(null);

    try {
      let result;
      switch (report) {
        case 'agendamentos':
          result = await getAgendamentos(filters, targetPage, 100);
          break;
        case 'consolidado-qtde':
          result = await getConsolidadoQtde(filters, targetPage, 100);
          break;
        case 'ultimas-3-contagens':
          result = await getUltimas3Contagens(filters, targetPage, 100);
          break;
        case 'ultimas-3-detalhado':
          result = await getUltimas3Detalhado(filters, targetPage, 100);
          break;
case 'consolidado-detalhado':
          result = await getConsolidadoDetalhado(filters, targetPage, 100);
          break;
        case 'segregacao-consolidado':
          result = await getSegregacaoConsolidado(filters, targetPage, 100);
          break;
        case 'solicitacao-coleta':
          result = await getSolicitacaoColeta(filters, targetPage, 100);
          break;
        case 'consolidado-leitura':
          result = await getConsolidadoLeitura(filters, targetPage, 100);
          break;
      }
      if (result) {
        setReportData(result.results || []);
        setTotalPages(result.total_pages || 0);
        setTotalRecords(result.total || 0);
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Erro ao gerar relatório');
    } finally {
      setLoading(false);
    }
  }, []);

  const generateReport = useCallback(async (filters: RelatorioFilters) => {
    if (!selectedReport) return;

    setPage(1);
    lastFiltersRef.current = filters;
    setReportData([]);

    await fetchReportPage(selectedReport, filters, 1);
  }, [selectedReport, fetchReportPage]);

  // Auto-fetch when page changes (pagination clicks)
  useEffect(() => {
    if (page > 1 && lastFiltersRef.current && selectedReport) {
      fetchReportPage(selectedReport, lastFiltersRef.current, page);
    }
  }, [page, selectedReport, fetchReportPage]);

  const exportExcel = useCallback(async (filters: RelatorioFilters) => {
    if (!selectedReport) return;

    setExporting(true);
    try {
      switch (selectedReport) {
        case 'agendamentos':
          await downloadAgendamentosExcel(filters);
          break;
        case 'consolidado-qtde':
          await downloadConsolidadoQtdeExcel(filters);
          break;
        case 'ultimas-3-contagens':
          await downloadUltimas3Excel(filters);
          break;
        case 'ultimas-3-detalhado':
          await downloadUltimas3DetalhadoExcel(filters);
          break;
case 'consolidado-detalhado':
          await downloadConsolidadoDetalhadoExcel(filters);
          break;
        case 'segregacao-consolidado':
          await downloadSegregacaoConsolidadoExcel(filters);
          break;
        case 'solicitacao-coleta':
          await downloadSolicitacaoColetaExcel(filters);
          break;
        case 'consolidado-leitura':
          await downloadConsolidadoLeituraExcel(filters);
          break;
      }
    } catch (err: any) {
      setError(err.message || 'Erro ao exportar Excel');
    } finally {
      setExporting(false);
    }
  }, [selectedReport]);

  // #12: export PDF (por enquanto: Consolidado qtde).
  const exportPdf = useCallback(async (filters: RelatorioFilters) => {
    if (selectedReport !== 'consolidado-qtde') return;
    setExporting(true);
    try {
      await downloadConsolidadoQtdePdf(filters);
    } catch (err: any) {
      setError(err.message || 'Erro ao exportar PDF');
    } finally {
      setExporting(false);
    }
  }, [selectedReport]);

  return {
    selectedReport,
    setSelectedReport,
    reportData,
    loading,
    error,
    exporting,
    page,
    setPage,
    totalPages,
    totalRecords,
    generateReport,
    exportExcel,
    exportPdf,
    hospitalOptions,
    clienteOptions,
    auditorOptions,
  };
}
